import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import wsClient from "@/lib/websocket";
import { getAccessToken, onAccessTokenChange } from "@/lib/tokenSession";
import { setWsConnected } from "@/redux/reducers/WebSocketReducer";

/**
 * Opens the one *authenticated* socket for a signed-in tab, and publishes
 * whether the tab's socket is up.
 *
 * The socket no longer carries the access token — it spends a single-use
 * ticket at connect — so a rotated token needs no rebuild. A token appearing
 * or disappearing still does: minting a ticket with no session is a 401.
 *
 * The authenticated socket is the only one this hook owns. A signed-out
 * visitor on the public career board holds a socket a `useLiveResource({
 * transport: "public" })` opened, and this hook sits above every page — so
 * `disconnect()` here closed that socket the moment the board mounted, and a
 * board that had just opened its own socket lost it (I1701 W6).
 * `disconnectAuth` closes an authenticated socket and leaves a public one
 * alone.
 *
 * The listener is registered either way, because the flag it publishes is
 * about whichever socket the tab actually holds.
 */
const useWsConnection = () => {
  const dispatch = useDispatch();
  const [token, setToken] = useState(getAccessToken);
  useEffect(() => onAccessTokenChange(setToken), []);

  useEffect(() => {
    if (token) wsClient.connect();
    else wsClient.disconnectAuth();

    return wsClient.listen((envelope) => {
      if (envelope?.event === "__open__") dispatch(setWsConnected(true));
      if (envelope?.event === "__close__") dispatch(setWsConnected(false));
    });
  }, [token, dispatch]);
};

export default useWsConnection;
